"use client";

import React, { useState, createContext, useContext, useId } from "react";
import Top5TracksModal from "./top-5-tracks-modal";
import { Track } from "@/types/track.types";
import { motion } from "framer-motion";

export type { Track };

interface TrackItem {
  track: Track;
}

interface Top5TracksListProps {
  items: TrackItem[];
  children: React.ReactNode;
}

interface TrackModalContextType {
  openModal: (track: Track) => void;
  getLayoutId: (trackName: string, type: string) => string;
}

const TrackModalContext = createContext<TrackModalContextType | null>(null);

export const useTrackModal = () => {
  const context = useContext(TrackModalContext);
  if (!context) {
    throw new Error("useTrackModal must be used within Top5TracksList");
  }
  return context;
};

export default function Top5TracksList({
  items,
  children,
}: Top5TracksListProps) {
  const [activeTrack, setActiveTrack] = useState<Track | null>(null);
  const id = useId();

  const openModal = (track: Track) => {
    setActiveTrack(track);
  };

  const getLayoutId = (trackName: string, type: string) => {
    return `${type}-${trackName}-${id}`;
  };

  return (
    <TrackModalContext.Provider value={{ openModal, getLayoutId }}>
      <Top5TracksModal
        track={activeTrack}
        onClose={() => setActiveTrack(null)}
        getLayoutId={getLayoutId}
      />
      <div className="flex flex-col space-y-3">
        {React.Children.map(children, (child, index) => {
          if (React.isValidElement(child) && items[index]) {
            const track = items[index].track;
            return (
              <motion.div
                layoutId={getLayoutId(track.name, "card")}
                key={`track-${track.name}`}
              >
                {child}
              </motion.div>
            );
          }
          return child;
        })}
      </div>
    </TrackModalContext.Provider>
  );
}
